import React, { lazy, Suspense } from 'react';
import { Routes, Route, useNavigate, useLocation, Link } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { Button } from '@mui/material';
import { AuthProvider } from './context/AuthProvider';
import { SocketProvider } from './context/SocketContext';
import { AdminMessageProvider } from './context/AdminMessageContext';
import { AdminLayout } from './components';
import RequireAuth from './components/shared/RequireAuth';
import AdminTranslationsLoader from './components/shared/AdminTranslationsLoader';
import { Spinner } from './components/shared/Spinner';
import { maybeReloadOnChunkError } from './utils/chunkReload';
import '@fontsource/space-grotesk';
import './css/admin/admin.css';

const ForgotPassword     = lazy(() => import('./screens/admin/ForgotPassword'));
const Home               = lazy(() => import('./screens/admin/Home'));
const Login              = lazy(() => import('./screens/admin/Login'));
const ResetPassword      = lazy(() => import('./screens/admin/ResetPassword'));
const MyProfile          = lazy(() => import('./screens/admin/MyProfile'));
const InviteRegisterPage = lazy(() => import('./screens/admin/panel/UserInviteRegister'));
const ReportIssue        = lazy(() => import('./screens/admin/ReportIssue'));
const NotFound           = lazy(() => import('./screens/admin/NotFound'));

const ClientPermissions    = lazy(() => import('./screens/admin/panel/ClientPermissions'));
const Permissions          = lazy(() => import('./screens/admin/panel/Permissions'));
const Roles                = lazy(() => import('./screens/admin/panel/Roles'));
const UserProfiles         = lazy(() => import('./screens/admin/panel/UserProfiles'));
const UserProfileEditor    = lazy(() => import('./screens/admin/panel/UserProfileEditor'));
const Users                = lazy(() => import('./screens/admin/panel/Users'));
const Invites              = lazy(() => import('./screens/admin/panel/UserInvites'));
const Translations         = lazy(() => import('./screens/admin/panel/Translations'));
const MyLab                = lazy(() => import('./screens/admin/panel/MyLab'));
const LabProjects          = lazy(() => import('./screens/admin/panel/LabProjects'));
const System               = lazy(() => import('./screens/admin/panel/System'));
const Updates              = lazy(() => import('./screens/admin/panel/Updates'));
const LabMembersPage       = lazy(() => import('./screens/admin/panel/LabMembersPage'));
const LabRoles             = lazy(() => import('./screens/admin/panel/LabRoles'));
const ResourceFiles        = lazy(() => import('./screens/admin/panel/ResourceFiles'));
const AssociationPage      = lazy(() => import('./screens/admin/panel/Association/AssociationPage'));
const Activities           = lazy(() => import('./screens/admin/panel/Activities/Activities'));
const ActivityEditorPage   = lazy(() => import('./screens/admin/panel/Activities/ActivityEditorPage'));
const Boardgames           = lazy(() => import('./screens/admin/panel/Boardgames/Boardgames'));
const BoardgameEditorPage  = lazy(() => import('./screens/admin/panel/Boardgames/BoardgameEditorPage'));
const Memberships          = lazy(() => import('./screens/admin/panel/Memberships/Memberships'));
const MembershipEditorPage = lazy(() => import('./screens/admin/panel/Memberships/MembershipEditorPage'));
const MemberXPBoard        = lazy(() => import('./screens/admin/panel/Memberships/MemberXPBoard'));
const Broadcasts           = lazy(() => import('./screens/admin/panel/Broadcasts/Broadcasts'));
const MyNotifications             = lazy(() => import('./screens/admin/panel/MyNotifications'));
const NotificationGroupSettings   = lazy(() => import('./screens/admin/panel/NotificationGroupSettings'));
const Curations            = lazy(() => import('./screens/admin/panel/Curations/Curations'));
const CurationEditorPage   = lazy(() => import('./screens/admin/panel/Curations/CurationEditorPage'));
const Sessions             = lazy(() => import('./screens/admin/panel/Sessions/Sessions'));
const SessionEditorPage    = lazy(() => import('./screens/admin/panel/Sessions/SessionEditorPage'));
const SessionViewPage      = lazy(() => import('./screens/admin/panel/Sessions/SessionViewPage'));
const Wiki                 = lazy(() => import('./screens/admin/panel/Wiki/Wiki'));
const LabIssues            = lazy(() => import('./screens/admin/panel/LabIssues/LabIssues'));
const Meetings             = lazy(() => import('./screens/admin/panel/Meetings/Meetings'));
const UndiscussedTopicsPage = lazy(() => import('./screens/admin/panel/Meetings/UndiscussedTopicsPage'));
const MeetingsList         = lazy(() => import('./screens/admin/panel/Meetings/MeetingsList'));
const MeetingEditorPage    = lazy(() => import('./screens/admin/panel/Meetings/MeetingEditorPage'));
const MeetingDetailPage    = lazy(() => import('./screens/admin/panel/Meetings/MeetingDetailPage'));
const Accounting           = lazy(() => import('./screens/admin/panel/Accounting/Accounting'));

const Admin = () => {
    const navigate = useNavigate();
    const location = useLocation();

    return (
        <ErrorBoundary
            FallbackComponent={(props) => (
                <ErrorFallback
                    {...props}
                    goBack={() => {
                        props.resetErrorBoundary();
                        navigate(-1);
                    }}
                />
            )}
            onError={(error) => {
                console.error(error);
                // After a redeploy, a stale tab can fail to load a renamed chunk
                // (blank page). Reload once to pull the fresh build.
                maybeReloadOnChunkError(error);
            }}
            resetKeys={[location.pathname]}
        >
            <AdminMessageProvider>
                <AuthProvider>
                    <SocketProvider>
                    <AdminTranslationsLoader />
                    <AdminLayout showAdminNavigation={true}>
                        <Suspense fallback={<Spinner />}>
                            <Routes>
                                {/* OPEN ROUTES*/}
                                <Route path='/' element={<Home/>}/>
                                <Route path='/login' element={<Login/>}/>
                                <Route path='/forgot-password' element={<ForgotPassword/>}/>
                                <Route path='/reset-password/:resetToken' element={<ResetPassword/>}/>
                                <Route path='/invite' element={<InviteRegisterPage/>}/>
                                <Route path='/report-issue' element={<ReportIssue/>}/>

                                {/* AUTH ROUTES*/}
                                <Route element={<RequireAuth/>}>
                                    <Route path='/account/profile' element={<MyProfile/>}/>
                                    <Route path='/client-permissions' element={<ClientPermissions/>}/>
                                    <Route path='/permissions' element={<Permissions/>}/>
                                    <Route path='/roles' element={<Roles/>}/>
                                    <Route path='/translations' element={<Translations/>}/>
                                    <Route path='/user-profiles' element={<UserProfiles/>}/>
                                    <Route path='/user-profiles/:id/edit' element={<UserProfileEditor/>}/>
                                    <Route path='/users' element={<Users/>}/>
                                    <Route path='/my-lab' element={<MyLab/>}/>
                                    <Route path='/lab-projects' element={<LabProjects/>}/>
                                    <Route path='/system' element={<System/>}/>
                                    <Route path='/updates' element={<Updates/>}/>
                                    <Route path='/lab-members' element={<LabMembersPage/>}/>
                                    <Route path='/lab-roles' element={<LabRoles/>}/>
                                    <Route path='/invites' element={<Invites/>}/>
                                    <Route path='/activities' element={<Activities/>}/>
                                    <Route path='/activities/new' element={<ActivityEditorPage/>}/>
                                    <Route path='/activities/:id/edit' element={<ActivityEditorPage/>}/>
                                    <Route path='/boardgames' element={<Boardgames/>}/>
                                    <Route path='/boardgames/new' element={<BoardgameEditorPage/>}/>
                                    <Route path='/boardgames/:id/edit' element={<BoardgameEditorPage/>}/>
                                    <Route path='/memberships' element={<Memberships/>}/>
                                    <Route path='/memberships/new' element={<MembershipEditorPage/>}/>
                                    <Route path='/memberships/:id/edit' element={<MembershipEditorPage/>}/>
                                    <Route path='/memberships/stats' element={<MemberXPBoard/>}/>
                                    <Route path='/broadcasts' element={<Broadcasts/>}/>
                                    <Route path='/my-notifications' element={<MyNotifications/>}/>
                                    <Route path='/notification-group-settings' element={<NotificationGroupSettings/>}/>
                                    <Route path='/curators' element={<Curations/>}/>
                                    <Route path='/curators/new' element={<CurationEditorPage/>}/>
                                    <Route path='/curators/:id/edit' element={<CurationEditorPage/>}/>
                                    <Route path='/sessions' element={<Sessions/>}/>
                                    <Route path='/sessions/new' element={<SessionEditorPage/>}/>
                                    <Route path='/sessions/:id/edit' element={<SessionEditorPage/>}/>
                                    <Route path='/sessions/:id/view' element={<SessionViewPage/>}/>
                                    <Route path='/resource-files' element={<ResourceFiles/>}/>
                                    <Route path='/association' element={<AssociationPage/>}/>
                                    <Route path='/wiki' element={<Wiki/>}/>
                                    <Route path='/lab-issues' element={<LabIssues/>}/>
                                    <Route path='/meetings' element={<Meetings/>}/>
                                    <Route path='/meetings-list' element={<MeetingsList/>}/>
                                    <Route path='/meetings/new' element={<MeetingEditorPage/>}/>
                                    <Route path='/meetings/undiscussed' element={<UndiscussedTopicsPage/>}/>
                                    <Route path='/meetings/:id/edit' element={<MeetingEditorPage/>}/>
                                    <Route path='/meetings/:id' element={<MeetingDetailPage/>}/>
                                    <Route path='/accounting' element={<Accounting/>}/>
                                </Route>

                                <Route path="*" element={<NotFound/>}/>
                            </Routes>
                        </Suspense>
                    </AdminLayout>
                    </SocketProvider>
                </AuthProvider>
            </AdminMessageProvider>
        </ErrorBoundary>
    );
};

function ErrorFallback({error, goBack}) {
    const errorMessage = error?.message || "Unknown error";

    const clearLocalStorage = () => {
        localStorage.removeItem('userInfo');
        localStorage.removeItem('userRole');
        goBack();
    };

    return (
        <AdminLayout showAdminNavigation={false}>
            <div className="main-user-error-message-block">
                <div className="user-error-message-block">
                    <p>Something went wrong 😭</p>
                    <span>Error: {errorMessage}</span>
                    <p>-</p>
                    <p>
                        <Link to={"/"} className="shop_go_to_frontend_link">
                            GO TO FRONTEND
                        </Link>
                    </p>
                    <p>
                        <Button onClick={goBack}>Go Back</Button>
                    </p>
                    <p>
                        <Button onClick={clearLocalStorage}>
                            Clear Local Storage and Go Back
                        </Button>
                    </p>
                </div>
            </div>
        </AdminLayout>
    );
}

export default Admin;
